import { t } from 'i18next';
import { Trans } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useLocalStorage } from 'usehooks-ts';

import { AuthErrorTypes } from 'authOld/constants';
import { authService } from 'authOld/services';
import { IAuthUser, IForgotPasswordForm, IResendEmailVerificationLinkForm } from 'authOld/types';
import { HeaderModalOptions } from 'coreOld/constants';
import LinkText from 'coreOld/elements/LinkText';
import { LocalStorageKeys, NotificationPosition } from 'coreOld/enums';
import { useHandleErrors } from 'coreOld/hooks';
import { actionLoadingMessage, actionMessage } from 'coreOld/utils';

import { useAuthModal } from './modal';

export const useAuthUser = () => {
  const [loggedUser, setLoggedUser] = useLocalStorage<IAuthUser>(
    LocalStorageKeys.LOGGED_USER,
    null
  );

  const queryClient = useQueryClient();

  const { data, isLoading, isFetched } = useQuery(
    ['authUser'],
    ({ signal }) => authService.getAuthUser(signal),
    {
      keepPreviousData: true,
      onSuccess: (data) => {
        setLoggedUser(data.record);
        queryClient.invalidateQueries(['colorsTheme', 'fontsTheme', 'fontFamiliesTheme']);
      },
    }
  );

  return {
    user: loggedUser || data?.record || null,
    isLoading: !loggedUser && isLoading,
    isFetched: !!loggedUser || isFetched,
  };
};

export const useAuthDefaultData = (enabled = true) => {
  const { data, isLoading, isFetched } = useQuery(
    ['authDefaultData'],
    ({ signal }) => authService.getDefaultData(signal),
    {
      keepPreviousData: true,
      enabled,
    }
  );

  return { authDefaultData: data?.defaultData, isLoading, isFetched };
};

export const useAuthActions = () => ({
  ...useSignIn(),
  ...useResendEmailVerificationLink(),
  ...useForgotPassword(),
  ...useCreatePassword(),
  ...useSignUp(),
  ...useSetUpAccount(),
  ...useCreateLocation(),
  ...useImpersonate(),
});

const useSignIn = () => {
  const { openAuthModal } = useAuthModal();

  const { errors } = useHandleErrors(
    null,
    t('actionProcessInProgress'),
    NotificationPosition.MODAL,
    [],
    undefined,
    undefined,
    ({ error, errorType }) => {
      if (AuthErrorTypes.ERROR_TYPE_USER_EMAIL_NOT_CONFIRMED !== errorType) {
        return '';
      }

      return (
        <Trans
          i18nKey="errorResendEmailVerificationLinkWithButton"
          values={{ error }}
          components={{
            linkText: (
              <LinkText
                onClick={() => openAuthModal(HeaderModalOptions.RESEND_EMAIL_VERIFICATION_LINK)}
              />
            ),
          }}
        />
      );
    }
  );

  const { mutateAsync } = useMutation(authService.signIn, {
    ...errors,
  });

  const signIn = (data: unknown) => mutateAsync(data as any);

  return { signIn };
};

const useResendEmailVerificationLink = () => {
  const { errors } = useHandleErrors(
    null,
    t('actionProcessInProgress'),
    NotificationPosition.MODAL
  );

  const { mutateAsync } = useMutation(authService.resendEmailVerificationLink, {
    ...errors,
  });

  const resendEmailVerificationLink = (data: IResendEmailVerificationLinkForm) => mutateAsync(data);

  return { resendEmailVerificationLink };
};

const useForgotPassword = () => {
  const { errors } = useHandleErrors(
    null,
    t('actionProcessInProgress'),
    NotificationPosition.MODAL
  );

  const { mutateAsync } = useMutation(authService.forgotPassword, {
    ...errors,
  });

  const forgotPassword = (data: IForgotPasswordForm) => mutateAsync(data);

  return { forgotPassword };
};

const useCreatePassword = () => {
  const { errors } = useHandleErrors(
    t('messageCreatePasswordSuccess'),
    t('actionProcessInProgress'),
    NotificationPosition.MODAL
  );

  const { mutateAsync } = useMutation(authService.createPassword, {
    ...errors,
  });

  const createPassword = (data) => mutateAsync(data);

  return { createPassword };
};

const useSignUp = () => {
  const { errors } = useHandleErrors(
    null,
    t('actionProcessInProgress'),
    NotificationPosition.MODAL
  );

  const { mutateAsync } = useMutation(authService.signUp, {
    ...errors,
  });

  const signUp = (data) => mutateAsync(data);

  return { signUp };
};

const useSetUpAccount = () => {
  const { errors } = useHandleErrors(
    t('messageSetUpAccountSuccess'),
    t('actionProcessInProgress'),
    NotificationPosition.MODAL
  );

  const { mutateAsync } = useMutation(authService.setUpAccount, {
    ...errors,
  });

  const setUpAccount = (data) => mutateAsync(data);

  return { setUpAccount };
};

const useCreateLocation = () => {
  const { errors } = useHandleErrors(
    actionMessage(t('labelLocation')),
    actionLoadingMessage(t('labelLocation').toLowerCase()),
    NotificationPosition.MODAL
  );

  const { mutateAsync } = useMutation(authService.createLocation, {
    ...errors,
  });

  const createLocation = (data) => mutateAsync(data);

  return { createLocation };
};

const useImpersonate = () => {
  const { errors } = useHandleErrors(
    null,
    t('actionProcessInProgress'),
    NotificationPosition.MODAL
  );

  const { mutateAsync } = useMutation(authService.impersonate, {
    ...errors,
  });

  const impersonate = (data) => mutateAsync(data);

  return { impersonate };
};
